import { useState } from "react";
import { commentItems } from "../../../mocks/hotNews";
import SectionHeading from "./SectionHeading";

const avatarMap: Record<string, string> = {
  primary: "bg-primary-500 text-background-50",
  accent: "bg-accent-500 text-foreground-950",
  secondary: "bg-secondary-500 text-foreground-950",
};

export default function Crowd() {
  const [liked, setLiked] = useState<Record<number, boolean>>({});
  const [extra, setExtra] = useState<Record<number, number>>({});

  const toggleLike = (id: number) => {
    setLiked((prev) => ({ ...prev, [id]: !prev[id] }));
    setExtra((prev) => ({ ...prev, [id]: (prev[id] || 0) + (liked[id] ? -1 : 1) }));
  };

  return (
    <section id="crowd" className="w-full bg-background-50 py-14 md:py-20">
      <div className="mx-auto w-full max-w-[1320px] px-4 md:px-6">
        <SectionHeading
          eyebrow="前排围观"
          title="吃瓜现场，评论区比正文还精彩"
          desc="从前排群众视角看热议进行时：点赞支持有趣的观点，也能顺着高赞评论看清这场讨论的风向走向。"
        />

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          <div className="relative flex min-h-[360px] flex-col justify-end overflow-hidden rounded-2xl p-6 lg:col-span-1">
            <img
              src="https://readdy.ai/api/search-image?query=Warm%20editorial%20collage%20of%20bright%20popcorn%20snack%20and%20smartphone%20with%20chat%20bubbles%20on%20a%20cream%20background%2C%20playful%20first%20row%20spectator%20theme%2C%20amber%20accent%20tones&width=1000&height=1200&seq=crowd-side-2026&orientation=portrait"
              alt="前排吃瓜围观话题讨论中"
              className="absolute inset-0 h-full w-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/90 to-transparent"></div>
            <div className="relative">
              <span className="mb-2 inline-flex items-center gap-1.5 rounded-full bg-accent-500 px-3 py-1 text-xs font-semibold text-foreground-950">
                <i className="ri-fire-fill"></i> 本周最热现场
              </span>
              <h3 className="font-heading text-2xl leading-snug text-background-50">
                “名场面二创接力”正在进行，三万人已前排就座
              </h3>
              <p className="mt-3 text-sm leading-relaxed text-foreground-200">
                一个临场反应引发的创作狂欢仍在发酵，最新一版“全员接龙”刚刚突破百万播放，讨论仍在加温。
              </p>
              <div className="mt-5 flex items-center gap-6 text-xs text-foreground-200">
                <span className="flex items-center gap-1.5">
                  <i className="ri-eye-line"></i> 312万围观
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-chat-3-line"></i> 4.8万讨论
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-thunderstorms-line text-accent-400"></i> 持续升温
                </span>
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-4 lg:col-span-2">
            <div className="flex items-center justify-between rounded-2xl border border-background-200 bg-background-100/60 px-5 py-4">
              <span className="flex items-center gap-2 text-sm font-medium text-foreground-800">
                <i className="ri-chat-forward-line text-lg text-primary-500"></i>
                正在热议的现场评论
              </span>
              <button type="button" className="flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700">
                我也要发言 <i className="ri-arrow-right-s-line"></i>
              </button>
            </div>

            {commentItems.map((c) => (
              <div
                key={c.id}
                className="flex gap-4 rounded-2xl border border-background-200 bg-background-50 p-4 transition-colors hover:border-primary-200 md:p-5"
              >
                <span
                  className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full font-heading text-sm ${avatarMap[c.avatarColor]}`}
                >
                  {c.nickname.slice(0, 2)}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                    <span className="text-sm font-bold text-foreground-900">{c.nickname}</span>
                    <span className="rounded-md bg-secondary-100 px-2 py-0.5 text-[11px] text-secondary-800">
                      前排 · {c.position}
                    </span>
                    <span className="ml-auto flex items-center gap-2 text-[11px] text-foreground-400">
                      <span className="flex items-center gap-1">
                        <i className="ri-chat-1-line"></i> {c.replies} 回复
                      </span>
                    </span>
                  </div>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-800">{c.text}</p>
                  <button
                    type="button"
                    onClick={() => toggleLike(c.id)}
                    className={`mt-3 flex items-center gap-1.5 whitespace-nowrap rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
                      liked[c.id]
                        ? "border-accent-500 bg-accent-100 text-accent-700"
                        : "border-background-200 text-foreground-600 hover:border-primary-300 hover:text-primary-600"
                    }`}
                  >
                    <i className={liked[c.id] ? "ri-thumb-up-fill" : "ri-thumb-up-line"}></i>
                    {(c.likes + (extra[c.id] || 0)).toLocaleString()}
                  </button>
                </div>
              </div>
            ))}

            <button
              type="button"
              className="whitespace-nowrap rounded-full border border-background-200 bg-background-100/50 py-3 text-sm font-medium text-foreground-700 transition-colors hover:bg-primary-100 hover:text-primary-700"
            >
              加载更多前排评论
              <i className="ri-arrow-down-s-line ml-1"></i>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}